'use client';

import {Asset} from '@/components/atoms';
import {useCommonComponentContext} from '@/context/ComponentContext';
import {getOnKeyDownProp} from '@/lib/utils/a11y';
import {CardLinkFieldsFragment} from '@common/hygraph-schema';
import {ChevronLeftIcon, ChevronRightIcon, CardProps, Card} from '@eds-web/react';
import {css, cx} from '@panda/css';
import {useFormatHtml} from '../text/Text.utils';
import {applySetLangToHref} from '@/lib/utils/setLang.utils';

const mediaQuery = 'lg';

const contentStyles = css({
	padding: '24px !important'
});

const descStyles = css({
	width: '90%',
	fontSize: 'eds.font.14',
	lineHeight: 'eds.lineHeight.140',
	sm: {
		fontSize: 'eds.font.12',
		lineHeight: 'eds.lineHeight.130'
	}
});

const iconStyles = css({
	display: 'none',
	[mediaQuery]: {
		display: 'block',
		position: 'absolute',
		top: '50%',
		right: '16px',
		transform: 'translateY(-50%)',
		lineHeight: '0'
	}
});

const imageStyles = css({
	height: '100%',
	[mediaQuery]: {
		borderTopRightRadius: '0 !important',
		borderBottomLeftRadius: 'eds.borderRadius.8 !important',
		width: '215px !important'
	}
});

const cardStyles = css({
	display: 'grid !important',
	gridTemplateColumns: {
		[mediaQuery]: '1fr 2fr',
		base: '1fr'
	},
	alignItems: 'center',
	bg: 'eds.background.elevated.high.default !important',
	_hover: {
		'& .card-link-icon > svg': {
			color: 'white !important'
		}
	}
});

type EdsCardProps = Omit<
	CardProps,
	'images' | 'imageMode' | 'clickOverlayProps' | 'description' | 'title'
>;

export interface CardLinkProps extends EdsCardProps, CardLinkFieldsFragment {
	className?: string;
}

export const CardLink = ({
	className,
	cardLink,
	title,
	description,
	asset,
	...props
}: CardLinkProps) => {
	const {isRTL} = useCommonComponentContext();
	const href = useFormatHtml(cardLink);

	return (
		<Card
			{...props}
			variant="solid"
			className={cx(className, cardStyles)}
			imagesClassName={imageStyles}
			contentClassName={contentStyles}
			clickOverlayProps={{href: applySetLangToHref(href)}}
			title={title}
			description={<div className={descStyles}>{description}</div>}
			imageMode="single"
			imageAspectRatio="16:9"
			images={<Asset asset={asset} />}
			customContent={
				<div className={cx(iconStyles, 'card-link-icon')}>
					{isRTL ? (
						<ChevronLeftIcon color="secondary" />
					) : (
						<ChevronRightIcon color="secondary" />
					)}
				</div>
			}
			onKeyDown={getOnKeyDownProp(() => {
				// Simulate a click for keyboard users.
				window.location.href = href;
			})}
		/>
	);
};
